import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Loader } from "@/components/AppShell";
import { toast } from "sonner";
import { BarChart3, MessageCircleQuestion, Sparkles, LogOut, Plus, Trash2, X, Flame, UserRound, Upload, Eye, ArrowUp, ArrowDown, Languages, Ticket, Copy, Check } from "lucide-react";

const AREAS = ["emozioni","relazioni","autostima","cambiamento","direzione","creativita","spiritualita","calma"];
const TABS = [["stats","Statistiche",BarChart3],["questions","Domande",MessageCircleQuestion],["symbols","Simboli",Sparkles],["chisono","Chi sono",UserRound],["ianas","Ianas",Flame],["inviti","Inviti personali",Ticket],["traduzioni","Traduzioni",Languages]];
const LANG_LABELS = { en: "English", fr: "Français", es: "Español", de: "Deutsch", it: "Italiano" };

export default function Admin() {
  const { user, loading, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("stats");

  if (loading) return <Loader />;
  if (!user || user.role !== "admin") { navigate("/"); return null; }

  return (
    <div className="anima-backdrop min-h-screen">
      <div className="max-w-2xl mx-auto px-6 py-10">
        <div className="flex items-center justify-between mb-8">
          <div>
            <p className="text-[11px] uppercase tracking-widest text-anima-muted">Pannello amministratore</p>
            <h1 className="font-serif text-4xl text-anima-ink">Ascolta Anima</h1>
          </div>
          <button onClick={async () => { await logout(); navigate("/"); }} className="p-2.5 rounded-full bg-white/60 border border-anima-ink/10" data-testid="admin-logout"><LogOut size={18} /></button>
        </div>

        <div className="flex gap-2 mb-8 overflow-x-auto no-scrollbar">
          {TABS.map(([k, l, Icon]) => (
            <button key={k} data-testid={`admin-tab-${k}`} onClick={() => setTab(k)}
              className={`flex items-center gap-2 rounded-full px-4 py-2.5 text-sm whitespace-nowrap border transition-colors duration-300 ${tab === k ? "bg-anima-sagedeep text-anima-ivory border-anima-sagedeep" : "bg-white/60 text-anima-ink border-anima-ink/10"}`}>
              <Icon size={15} /> {l}
            </button>
          ))}
        </div>

        {tab === "stats" && <Stats />}
        {tab === "questions" && <Questions />}
        {tab === "symbols" && <Symbols />}
        {tab === "chisono" && <ChiSonoAdmin />}
        {tab === "ianas" && <Ianas />}
        {tab === "inviti" && <InvitiPersonali />}
        {tab === "traduzioni" && <Translations />}
      </div>
    </div>
  );
}

function Stats() {
  const [s, setS] = useState(null);
  useEffect(() => { api.get("/admin/stats").then((r) => setS(r.data)); }, []);
  if (!s) return <Loader />;
  const cards = [
    ["Utenti", s.total_users], ["Riflessioni", s.total_entries],
    ["Abbonamenti attivi", s.active_subscriptions], ["In prova", s.trialing],
    ["Attivi (7 giorni)", s.active_last_7_days],
  ];
  return (
    <div>
      <SubscriptionsOverview />
      <div className="grid grid-cols-2 gap-4 mb-8">
        {cards.map(([l, v]) => (
          <div key={l} className="rounded-3xl bg-white/60 border border-anima-ink/5 p-5">
            <p className="font-serif text-4xl text-anima-ink">{v}</p>
            <p className="text-sm text-anima-muted">{l}</p>
          </div>
        ))}
      </div>
      <p className="text-[11px] uppercase tracking-widest text-anima-muted mb-3">Aree esplorate (anonimo)</p>
      <div className="space-y-2">
        {Object.entries(s.areas || {}).sort((a, b) => b[1] - a[1]).map(([a, c]) => (
          <div key={a} className="flex items-center gap-3">
            <span className="w-28 text-sm text-anima-ink capitalize">{a}</span>
            <div className="flex-1 h-2 rounded-full bg-anima-ink/5 overflow-hidden">
              <div className="h-full bg-anima-sage" style={{ width: `${Math.min(100, (c / Math.max(...Object.values(s.areas))) * 100)}%` }} />
            </div>
            <span className="text-sm text-anima-muted w-6 text-right">{c}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

const euro = (n) => new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR" }).format(n || 0);

function SubscriptionsOverview() {
  const [o, setO] = useState(null);
  useEffect(() => { api.get("/admin/subscriptions-overview").then((r) => setO(r.data)).catch(() => setO(false)); }, []);
  if (o === null) return <Loader />;
  if (o === false) return <p className="text-sm text-anima-muted mb-8" data-testid="subs-overview-error">Riepilogo abbonamenti non disponibile.</p>;
  const cur = o.revenue?.current, prev = o.revenue?.previous;
  const cards = [
    ["Abbonati attivi", o.active_total, `${o.active_monthly} mensili · ${o.active_yearly} annuali`, "subs-active"],
    ["Prova in corso", o.trial_valid, "7 giorni non ancora scaduti", "subs-trial-valid"],
    ["Prova scaduta", o.trial_expired, "senza piano scelto", "subs-trial-expired"],
  ];
  return (
    <section data-testid="subs-overview" className="mb-10">
      <p className="text-[11px] uppercase tracking-widest text-anima-muted mb-3">Abbonamenti</p>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4">
        {cards.map(([l, v, d, id]) => (
          <div key={id} data-testid={id} className="rounded-3xl bg-white/60 border border-anima-ink/5 p-5">
            <p className="font-serif text-4xl text-anima-ink">{v}</p>
            <p className="text-sm text-anima-ink">{l}</p>
            <p className="text-xs text-anima-muted">{d}</p>
          </div>
        ))}
      </div>
      <div data-testid="subs-revenue" className="rounded-3xl bg-white/60 border border-anima-ink/5 p-5">
        <div className="flex items-baseline justify-between mb-3">
          <p className="text-sm text-anima-ink">Incassi del mese {o.month_label}</p>
          {o.revenue && <span className="text-xs text-anima-muted">{o.revenue.livemode ? "Stripe Live" : "Stripe Test"}</span>}
        </div>
        {o.revenue_error ? <p className="text-sm text-anima-muted" data-testid="subs-revenue-error">{o.revenue_error}</p> : (
          <>
            <p className="font-serif text-4xl text-anima-ink" data-testid="subs-revenue-net">{euro(cur.net)}</p>
            <p className="text-xs text-anima-muted mb-3">netto dei rimborsi, prima delle commissioni Stripe</p>
            <div className="grid grid-cols-3 gap-3 text-sm">
              <div><p className="text-anima-muted text-xs">Lordo</p><p className="text-anima-ink" data-testid="subs-revenue-gross">{euro(cur.gross)}</p></div>
              <div><p className="text-anima-muted text-xs">Rimborsi</p><p className="text-anima-ink" data-testid="subs-revenue-refunded">{euro(cur.refunded)}</p></div>
              <div><p className="text-anima-muted text-xs">Pagamenti</p><p className="text-anima-ink" data-testid="subs-revenue-count">{cur.payments}</p></div>
            </div>
            <p className="text-xs text-anima-muted mt-4 pt-3 border-t border-anima-ink/5" data-testid="subs-revenue-prev">
              Mese precedente ({o.prev_month_label}): {euro(prev.net)} netti · {prev.payments} pagamenti
            </p>
          </>
        )}
      </div>
    </section>
  );
}

function Questions() {
  const [items, setItems] = useState(null);
  const [form, setForm] = useState({ text: "", area: "emozioni" });
  const load = () => api.get("/admin/questions").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const add = async () => {
    if (!form.text.trim()) return;
    try { await api.post("/admin/questions", { ...form, tags: [] }); setForm({ text: "", area: "emozioni" }); load(); toast.success("Domanda aggiunta"); }
    catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  };
  const toggle = async (q) => { await api.put(`/admin/questions/${q.id}`, { active: !q.active }); load(); };
  const del = async (q) => { if (!window.confirm("Eliminare la domanda?")) return; await api.delete(`/admin/questions/${q.id}`); load(); };

  if (!items) return <Loader />;
  return (
    <div>
      <div className="rounded-3xl bg-white/60 border border-anima-ink/5 p-5 mb-6">
        <textarea data-testid="admin-q-text" value={form.text} onChange={(e) => setForm({ ...form, text: e.target.value })} rows={2}
          placeholder="Nuova domanda aperta..." className="w-full bg-transparent outline-none font-serif text-lg resize-none" />
        <div className="flex items-center gap-2 mt-2">
          <select data-testid="admin-q-area" value={form.area} onChange={(e) => setForm({ ...form, area: e.target.value })} className="rounded-full bg-white border border-anima-ink/10 px-3 py-2 text-sm">
            {AREAS.map((a) => <option key={a} value={a}>{a}</option>)}
          </select>
          <button data-testid="admin-q-add" onClick={add} className="ml-auto rounded-full bg-anima-sagedeep text-anima-ivory px-5 py-2.5 text-sm flex items-center gap-1.5"><Plus size={15} /> Aggiungi</button>
        </div>
      </div>
      <div className="space-y-3">
        {items.map((q) => (
          <div key={q.id} className="rounded-2xl bg-white/55 border border-anima-ink/5 p-4 flex items-start gap-3">
            <div className="flex-1">
              <p className="font-serif text-lg text-anima-ink leading-tight">{q.text}</p>
              <span className="text-xs text-anima-muted capitalize">{q.area}{q.is_demo ? " \u00b7 demo" : ""}</span>
            </div>
            <button onClick={() => toggle(q)} className={`text-xs rounded-full px-3 py-1 ${q.active ? "bg-anima-sage/30 text-anima-ink" : "bg-anima-ink/10 text-anima-muted"}`}>{q.active ? "attiva" : "off"}</button>
            <button onClick={() => del(q)} className="text-anima-muted p-1"><Trash2 size={16} /></button>
          </div>
        ))}
      </div>
    </div>
  );
}

function Symbols() {
  const [items, setItems] = useState(null);
  const [edit, setEdit] = useState(null);
  const [creating, setCreating] = useState(false);
  const [kw, setKw] = useState("");
  const [qs, setQs] = useState("");
  const [ps, setPs] = useState("");
  const load = () => api.get("/admin/symbols").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const openEdit = (s) => {
    setCreating(false); setEdit({ ...s }); setKw((s.keywords || []).join(", "));
    setQs((s.questions || (s.question ? [s.question] : [])).join("\n"));
    setPs((s.practices || (s.gesture ? [s.gesture] : [])).join("\n"));
  };
  const openCreate = () => {
    setCreating(true);
    setEdit({ key: "", name: "", meaning: "", evokes: "", accompanies: "", phrase: "", theme: "", image_url: "", active: true });
    setKw(""); setQs(""); setPs("");
  };

  const save = async () => {
    const keywords = kw.split(",").map((k) => k.trim()).filter(Boolean);
    const questions = qs.split("\n").map((x) => x.trim()).filter(Boolean);
    const practices = ps.split("\n").map((x) => x.trim()).filter(Boolean);
    const payload = {
      name: edit.name, meaning: edit.meaning, image_url: edit.image_url, keywords,
      evokes: edit.evokes || "", accompanies: edit.accompanies || "",
      phrase: edit.phrase || "", theme: edit.theme || "",
      questions, practices,
      question: questions[0] || "", gesture: practices[0] || "", active: edit.active !== false,
    };
    try {
      if (creating) {
        if (!edit.key.trim() || !edit.name.trim()) { toast.error("Chiave e nome sono obbligatori"); return; }
        await api.post("/admin/symbols", { ...payload, key: edit.key.trim().toLowerCase() });
        toast.success("Simbolo creato");
      } else {
        await api.put(`/admin/symbols/${edit.id}`, payload);
        toast.success("Simbolo aggiornato");
      }
      setEdit(null); load();
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  };

  const onImage = (e) => {
    const file = e.target.files?.[0];
    if (!file || !file.type.startsWith("image/")) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      const img = new Image();
      img.onload = () => {
        const maxW = 900;
        const scale = Math.min(1, maxW / img.width);
        const canvas = document.createElement("canvas");
        canvas.width = Math.round(img.width * scale);
        canvas.height = Math.round(img.height * scale);
        canvas.getContext("2d").drawImage(img, 0, 0, canvas.width, canvas.height);
        setEdit((p) => ({ ...p, image_url: canvas.toDataURL("image/jpeg", 0.85) }));
        toast.success("Immagine pronta — ricorda di salvare");
      };
      img.src = ev.target.result;
    };
    reader.readAsDataURL(file);
  };

  const move = async (idx, dir) => {
    const next = [...items];
    const j = idx + dir;
    if (j < 0 || j >= next.length) return;
    [next[idx], next[j]] = [next[j], next[idx]];
    setItems(next);
    try { await api.put("/admin/symbols/order", { ids: next.map((s) => s.id) }); }
    catch { toast.error("Errore nel riordino"); load(); }
  };

  if (!items) return <Loader />;
  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-anima-muted pr-3">Tocca un simbolo per modificarlo. Usa le frecce per l'ordine.</p>
        <button data-testid="admin-symbol-add" onClick={openCreate} className="shrink-0 flex items-center gap-1.5 rounded-full bg-anima-sagedeep text-anima-ivory px-4 py-2 text-sm"><Plus size={15} /> Aggiungi</button>
      </div>
      <p className="text-xs text-anima-muted mb-4">{items.length} simboli totali ({items.filter((s) => s.active !== false).length} attivi)</p>
      <div className="grid grid-cols-2 gap-4">
        {items.map((s, i) => (
          <div key={s.id} className="rounded-3xl overflow-hidden bg-white/55 border border-anima-ink/5">
            <button data-testid={`admin-symbol-${s.key}`} onClick={() => openEdit(s)} className="block w-full text-left">
              <img src={s.image_url} alt={s.name} className="w-full h-28 object-cover" />
              <div className="px-3 pt-2 flex items-center justify-between">
                <p className="font-serif text-lg text-anima-ink">{s.name}</p>
                {s.active === false && <span className="text-[10px] text-anima-muted bg-anima-ink/10 rounded-full px-2 py-0.5">off</span>}
              </div>
            </button>
            <div className="flex items-center justify-end gap-1 px-3 pb-2">
              <button data-testid={`admin-symbol-up-${s.key}`} onClick={() => move(i, -1)} disabled={i === 0} className="p-1 text-anima-muted disabled:opacity-30"><ArrowUp size={15} /></button>
              <button data-testid={`admin-symbol-down-${s.key}`} onClick={() => move(i, 1)} disabled={i === items.length - 1} className="p-1 text-anima-muted disabled:opacity-30"><ArrowDown size={15} /></button>
            </div>
          </div>
        ))}
      </div>
      {edit && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-anima-ink/30 backdrop-blur-sm px-5 py-6" onClick={() => setEdit(null)}>
          <div className="w-full max-w-md bg-anima-ivory rounded-3xl p-6 max-h-[88vh] overflow-y-auto no-scrollbar" onClick={(e) => e.stopPropagation()} data-testid="admin-symbol-edit">
            <div className="flex items-center justify-between mb-4">
              <p className="font-serif text-2xl text-anima-ink">{creating ? "Nuovo simbolo" : "Modifica simbolo"}</p>
              <button onClick={() => setEdit(null)}><X size={20} className="text-anima-muted" /></button>
            </div>
            {edit.image_url && <img src={edit.image_url} alt="" className="w-full h-40 object-cover rounded-2xl mb-3" />}
            <label className="inline-flex items-center gap-2 rounded-full bg-anima-pink/60 text-anima-ink px-4 py-2 text-sm cursor-pointer mb-3" data-testid="admin-symbol-upload-label">
              <Upload size={14} /> Carica immagine
              <input type="file" accept="image/*" className="hidden" onChange={onImage} data-testid="admin-symbol-upload" />
            </label>
            {creating && (
              <>
                <label className="text-xs text-anima-muted">Chiave univoca (senza spazi, es. "quercia")</label>
                <input data-testid="admin-symbol-key" value={edit.key} onChange={(e) => setEdit({ ...edit, key: e.target.value })} className="symadm-in" />
              </>
            )}
            <label className="text-xs text-anima-muted">Nome</label>
            <input value={edit.name} onChange={(e) => setEdit({ ...edit, name: e.target.value })} className="symadm-in" />
            <label className="text-xs text-anima-muted">Tema (per il filtro)</label>
            <input data-testid="admin-symbol-theme" value={edit.theme || ""} onChange={(e) => setEdit({ ...edit, theme: e.target.value })} className="symadm-in" />
            <label className="text-xs text-anima-muted">Frase evocativa</label>
            <input data-testid="admin-symbol-phrase" value={edit.phrase || ""} onChange={(e) => setEdit({ ...edit, phrase: e.target.value })} className="symadm-in" />
            <label className="text-xs text-anima-muted">Parole chiave (separate da virgola)</label>
            <input data-testid="admin-symbol-keywords" value={kw} onChange={(e) => setKw(e.target.value)} className="symadm-in" />
            <label className="text-xs text-anima-muted">Significato (sintesi)</label>
            <textarea value={edit.meaning} onChange={(e) => setEdit({ ...edit, meaning: e.target.value })} rows={2} className="symadm-in" />
            <label className="text-xs text-anima-muted">Ciò che può evocare</label>
            <textarea data-testid="admin-symbol-evokes" value={edit.evokes || ""} onChange={(e) => setEdit({ ...edit, evokes: e.target.value })} rows={3} className="symadm-in" />
            <label className="text-xs text-anima-muted">Quando può accompagnarti</label>
            <textarea data-testid="admin-symbol-accompanies" value={edit.accompanies || ""} onChange={(e) => setEdit({ ...edit, accompanies: e.target.value })} rows={3} className="symadm-in" />
            <label className="text-xs text-anima-muted">Domande aperte (una per riga)</label>
            <textarea data-testid="admin-symbol-questions" value={qs} onChange={(e) => setQs(e.target.value)} rows={3} className="symadm-in" />
            <label className="text-xs text-anima-muted">Pratiche / piccoli gesti (una per riga)</label>
            <textarea data-testid="admin-symbol-practices" value={ps} onChange={(e) => setPs(e.target.value)} rows={2} className="symadm-in" />
            <label className="text-xs text-anima-muted">URL immagine (alternativo al caricamento)</label>
            <input data-testid="admin-symbol-image" value={edit.image_url} onChange={(e) => setEdit({ ...edit, image_url: e.target.value })} className="symadm-in text-sm" />
            <div className="flex items-center justify-between rounded-2xl bg-white/60 border border-anima-ink/5 px-4 py-3 my-3">
              <span className="text-sm text-anima-ink">Simbolo attivo</span>
              <button data-testid="admin-symbol-active" onClick={() => setEdit({ ...edit, active: edit.active === false ? true : false })}
                className={`w-12 h-7 rounded-full relative transition-colors duration-300 ${edit.active !== false ? "bg-anima-sagedeep" : "bg-anima-ink/15"}`}>
                <span className={`absolute top-0.5 w-6 h-6 rounded-full bg-white transition-all duration-300 ${edit.active !== false ? "left-[22px]" : "left-0.5"}`} />
              </button>
            </div>
            {!creating && (
              <a href={`/app/simboli/${edit.key}?preview=1`} target="_blank" rel="noreferrer" data-testid="admin-symbol-preview"
                className="flex items-center justify-center gap-2 rounded-full border border-anima-ink/15 text-anima-ink py-3 mb-3 text-sm"><Eye size={15} /> Anteprima pagina</a>
            )}
            <button data-testid="admin-symbol-save" onClick={save} className="w-full rounded-full bg-anima-sagedeep text-anima-ivory py-3">{creating ? "Crea simbolo" : "Salva"}</button>
            <style>{`.symadm-in{width:100%;margin:4px 0 12px;border-radius:12px;background:rgba(255,255,255,0.6);border:1px solid rgba(74,69,67,0.12);padding:10px 12px;outline:none;font-size:14px}`}</style>
          </div>
        </div>
      )}
    </div>
  );
}

function ChiSonoAdmin() {
  const [d, setD] = useState(null);
  const [published, setPublished] = useState(false);
  const load = () => api.get("/admin/bio").then((r) => { setD(r.data?.draft || {}); setPublished(!!r.data?.page_enabled); });
  useEffect(() => { load(); }, []);

  const save = async (patch) => {
    try { await api.put("/admin/bio", patch); toast.success("Bozza salvata"); }
    catch { toast.error("Errore nel salvataggio"); }
  };
  const setField = (k, v) => setD((p) => ({ ...p, [k]: v }));
  const saveField = (k) => save({ [k]: d[k] });

  const publish = async () => {
    try { await api.post("/admin/bio/publish"); setPublished(true); toast.success("Pagina pubblicata"); }
    catch { toast.error("Errore nella pubblicazione"); }
  };

  const onPortrait = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Seleziona un'immagine"); return; }
    const reader = new FileReader();
    reader.onload = (ev) => {
      const img = new Image();
      img.onload = () => {
        const maxW = 1000;
        const scale = Math.min(1, maxW / img.width);
        const canvas = document.createElement("canvas");
        canvas.width = Math.round(img.width * scale);
        canvas.height = Math.round(img.height * scale);
        canvas.getContext("2d").drawImage(img, 0, 0, canvas.width, canvas.height);
        const dataUrl = canvas.toDataURL("image/jpeg", 0.86);
        setField("portrait_url", dataUrl);
        save({ portrait_url: dataUrl });
        toast.success("Ritratto caricato — ricorda di pubblicare");
      };
      img.src = ev.target.result;
    };
    reader.readAsDataURL(file);
  };

  const setSection = (i, key, val) => {
    const next = [...(d.sections || [])];
    next[i] = { ...next[i], [key]: val };
    setField("sections", next);
  };
  const setLink = (i, key, val) => {
    const next = [...(d.links || [])];
    next[i] = { ...next[i], [key]: val };
    setField("links", next);
    if (key === "enabled") save({ links: next });
  };

  if (!d) return <Loader />;

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between rounded-3xl bg-white/60 border border-anima-ink/5 p-5">
        <div>
          <p className="font-serif text-2xl text-anima-ink">Anna Checchi</p>
          <p className="text-xs text-anima-muted mt-0.5">{published ? "Pagina pubblicata" : "Pagina non pubblicata"}</p>
        </div>
        <div className="flex gap-2">
          <a href="/chi-sono?preview=1" target="_blank" rel="noreferrer" data-testid="chisono-preview-btn"
            className="flex items-center gap-1.5 rounded-full border border-anima-ink/15 text-anima-ink px-4 py-2.5 text-sm"><Eye size={15} /> Anteprima</a>
          <button onClick={publish} data-testid="chisono-publish-btn"
            className="rounded-full bg-anima-sagedeep text-anima-ivory px-4 py-2.5 text-sm">Pubblica</button>
        </div>
      </div>

      <div className="rounded-3xl bg-white/60 border border-anima-ink/5 p-5 space-y-4">
        <p className="text-[11px] uppercase tracking-widest text-anima-muted">Ritratto</p>
        {d.portrait_url && (
          <img src={d.portrait_url} alt="Ritratto Anna Checchi" className="w-28 h-auto rounded-2xl border border-anima-ink/10" draggable={false} onContextMenu={(e) => e.preventDefault()} />
        )}
        <label className="inline-flex items-center gap-2 rounded-full bg-anima-pink/60 text-anima-ink px-5 py-2.5 text-sm cursor-pointer w-fit" data-testid="chisono-upload-label">
          <Upload size={15} /> {d.portrait_url ? "Sostituisci ritratto" : "Carica ritratto"}
          <input type="file" accept="image/*" className="hidden" onChange={onPortrait} data-testid="chisono-portrait-input" />
        </label>
        <Fld label="Testo alternativo del ritratto"><input data-testid="chisono-alt" value={d.portrait_alt || ""} onChange={(e) => setField("portrait_alt", e.target.value)} onBlur={() => saveField("portrait_alt")} className="adm-in" /></Fld>
      </div>

      <Fld label="Sottotitolo"><input data-testid="chisono-subtitle" value={d.subtitle || ""} onChange={(e) => setField("subtitle", e.target.value)} onBlur={() => saveField("subtitle")} className="adm-in" /></Fld>
      <Fld label="Qualifiche professionali"><textarea data-testid="chisono-qualifications" rows={2} value={d.qualifications || ""} onChange={(e) => setField("qualifications", e.target.value)} onBlur={() => saveField("qualifications")} className="adm-in" /></Fld>
      <Fld label="Introduzione"><textarea data-testid="chisono-intro" rows={2} value={d.intro || ""} onChange={(e) => setField("intro", e.target.value)} onBlur={() => saveField("intro")} className="adm-in" /></Fld>

      <div className="rounded-3xl bg-white/50 border border-anima-ink/5 p-5 space-y-4">
        <p className="text-[11px] uppercase tracking-widest text-anima-muted">Biografia</p>
        {(d.sections || []).map((s, i) => (
          <div key={`${i}-${s.title || "sec"}`} className="space-y-2 border-b border-anima-ink/8 pb-4 last:border-0 last:pb-0">
            <input data-testid={`chisono-section-title-${i}`} value={s.title || ""} onChange={(e) => setSection(i, "title", e.target.value)} onBlur={() => save({ sections: d.sections })} className="adm-in font-serif" placeholder="Titolo sezione" />
            <textarea data-testid={`chisono-section-body-${i}`} rows={5} value={s.body || ""} onChange={(e) => setSection(i, "body", e.target.value)} onBlur={() => save({ sections: d.sections })} className="adm-in" placeholder="Testo" />
          </div>
        ))}
      </div>

      <Fld label="Citazione in evidenza"><textarea data-testid="chisono-quote" rows={3} value={d.quote_text || ""} onChange={(e) => setField("quote_text", e.target.value)} onBlur={() => saveField("quote_text")} className="adm-in" /></Fld>
      <Fld label="Firma della citazione"><input data-testid="chisono-quote-author" value={d.quote_author || ""} onChange={(e) => setField("quote_author", e.target.value)} onBlur={() => saveField("quote_author")} className="adm-in" /></Fld>

      <div className="rounded-3xl bg-white/50 border border-anima-ink/5 p-5 space-y-4">
        <p className="text-[11px] uppercase tracking-widest text-anima-muted">Collegamenti professionali</p>
        {(d.links || []).map((l, i) => (
          <div key={l.key} className="space-y-2 border-b border-anima-ink/8 pb-4 last:border-0 last:pb-0">
            <div className="flex items-center justify-between">
              <input data-testid={`chisono-link-label-${l.key}`} value={l.label || ""} onChange={(e) => setLink(i, "label", e.target.value)} onBlur={() => save({ links: d.links })} className="adm-in flex-1 mr-3" placeholder="Etichetta" />
              <button data-testid={`chisono-link-toggle-${l.key}`} onClick={() => setLink(i, "enabled", !l.enabled)}
                className={`w-12 h-7 rounded-full relative transition-colors duration-300 shrink-0 ${l.enabled ? "bg-anima-sagedeep" : "bg-anima-ink/15"}`}>
                <span className={`absolute top-0.5 w-6 h-6 rounded-full bg-white transition-all duration-300 ${l.enabled ? "left-[22px]" : "left-0.5"}`} />
              </button>
            </div>
            <input data-testid={`chisono-link-url-${l.key}`} value={l.url || ""} onChange={(e) => setLink(i, "url", e.target.value)} onBlur={() => save({ links: d.links })} className="adm-in" placeholder="https://…" />
          </div>
        ))}
        <p className="text-xs text-anima-muted">Un collegamento appare nella pagina solo se attivo e con un indirizzo valido.</p>
      </div>

      <div className="rounded-2xl bg-anima-gold/10 border border-anima-gold/25 px-5 py-4">
        <p className="text-sm text-anima-ink">Le modifiche vengono salvate come bozza. Usa <b>Anteprima</b> per controllare e <b>Pubblica</b> per rendere visibili le modifiche.</p>
      </div>
      <style>{`.adm-in{width:100%;margin-top:4px;border-radius:12px;background:rgba(255,255,255,0.6);border:1px solid rgba(74,69,67,0.12);padding:10px 12px;outline:none;font-size:14px}`}</style>
    </div>
  );
}

function Ianas() {
  const [cfg, setCfg] = useState(null);
  const load = () => api.get("/admin/ianas").then((r) => setCfg(r.data));
  useEffect(() => { load(); }, []);
  const save = async (patch) => {
    try { const { data } = await api.put("/admin/ianas", patch); setCfg({ ...data, uses: cfg.uses, symbol_ai_enabled: patch.ai_enabled ?? cfg.symbol_ai_enabled }); toast.success("Salvato"); load(); }
    catch (e) { toast.error("Errore nel salvataggio"); }
  };
  if (!cfg) return <Loader />;
  const set = (k, v) => setCfg({ ...cfg, [k]: v });
  return (
    <div className="space-y-5">
      <div className="rounded-3xl bg-white/60 border border-anima-ink/5 p-5">
        <div className="flex gap-4">
          <img src={cfg.detail_url} alt="Dettaglio Fuoco dello Spirito" className="w-20 h-28 object-cover rounded-2xl" draggable={false} onContextMenu={(e) => e.preventDefault()} />
          <img src={cfg.image_url} alt="Ianas" className="w-20 h-28 object-cover rounded-2xl" draggable={false} onContextMenu={(e) => e.preventDefault()} />
          <div className="flex-1">
            <p className="font-serif text-2xl text-anima-ink">{cfg.title}</p>
            <p className="text-sm text-anima-muted">{cfg.author}</p>
            <p className="text-xs text-anima-muted mt-1">{cfg.technique} · {cfg.size} · {cfg.year}</p>
          </div>
        </div>
      </div>

      <Fld label="URL immagine opera (versione web)"><input data-testid="ianas-image-url" value={cfg.image_url || ""} onChange={(e) => set("image_url", e.target.value)} onBlur={(e) => save({ image_url: e.target.value })} className="adm-in" /></Fld>
      <Fld label="URL dettaglio Fuoco dello Spirito"><input data-testid="ianas-detail-url" value={cfg.detail_url || ""} onChange={(e) => set("detail_url", e.target.value)} onBlur={(e) => save({ detail_url: e.target.value })} className="adm-in" /></Fld>
      <Fld label="Didascalia dettaglio"><input data-testid="ianas-caption" value={cfg.caption || ""} onChange={(e) => set("caption", e.target.value)} onBlur={(e) => save({ caption: e.target.value })} className="adm-in" /></Fld>
      <Fld label="Testo narrativo (opzionale, sovrascrive quello predefinito)"><textarea data-testid="ianas-narrative" rows={4} value={cfg.narrative || ""} onChange={(e) => set("narrative", e.target.value)} onBlur={(e) => save({ narrative: e.target.value })} className="adm-in" /></Fld>
      <Fld label="Copyright"><input value={cfg.copyright || ""} onChange={(e) => set("copyright", e.target.value)} onBlur={(e) => save({ copyright: e.target.value })} className="adm-in" /></Fld>

      <Toggle label="Pagina 'La storia di Ianas' attiva" testid="ianas-page-toggle" on={cfg.page_enabled} onClick={() => save({ page_enabled: !cfg.page_enabled })} />
      <Toggle label="Il simbolo può essere scelto dall'AI" testid="ianas-ai-toggle" on={cfg.ai_enabled} onClick={() => save({ ai_enabled: !cfg.ai_enabled })} />

      <Fld label="Frequenza massima di comparsa (giorni)"><input data-testid="ianas-frequency" type="number" value={cfg.max_frequency_days ?? 14} onChange={(e) => set("max_frequency_days", e.target.value)} onBlur={(e) => save({ max_frequency_days: e.target.value })} className="adm-in" /></Fld>

      <div className="rounded-3xl bg-white/50 border border-anima-ink/5 p-5">
        <p className="text-[11px] uppercase tracking-widest text-anima-muted mb-2">Dove viene usata l'opera</p>
        <div className="space-y-2">
          {(cfg.uses || []).map((u, i) => (
            <div key={u.location || `use-${i}`} className="flex items-center justify-between text-sm">
              <span className="text-anima-ink">{u.location}</span>
              <span className="text-anima-muted text-xs">{u.type}{u.enabled === false ? " · disattivato" : ""}</span>
            </div>
          ))}
        </div>
      </div>
      <style>{`.adm-in{width:100%;margin-top:4px;border-radius:12px;background:rgba(255,255,255,0.6);border:1px solid rgba(74,69,67,0.12);padding:10px 12px;outline:none;font-size:14px}`}</style>
    </div>
  );
}

function Fld({ label, children }) {
  return (<div><label className="text-xs text-anima-muted ml-1">{label}</label>{children}</div>);
}
function Toggle({ label, on, onClick, testid }) {
  return (
    <div className="flex items-center justify-between rounded-2xl bg-white/60 border border-anima-ink/5 px-5 py-3.5">
      <span className="text-sm text-anima-ink">{label}</span>
      <button data-testid={testid} onClick={onClick} className={`w-12 h-7 rounded-full relative transition-colors duration-300 ${on ? "bg-anima-sagedeep" : "bg-anima-ink/15"}`}>
        <span className={`absolute top-0.5 w-6 h-6 rounded-full bg-white transition-all duration-300 ${on ? "left-[22px]" : "left-0.5"}`} />
      </button>
    </div>
  );
}

const INVITE_STATUS = { available: ["Disponibile", "text-anima-sagedeep bg-anima-sage/25"], used: ["Utilizzato", "text-anima-golddeep bg-anima-gold/20"], expired: ["Scaduto", "text-anima-muted bg-anima-ink/8"], disabled: ["Disattivato", "text-anima-muted bg-anima-ink/8"] };
const PLAN_LABELS = { anima_monthly: "Mensile", anima_yearly: "Annuale" };

function InvitiPersonali() {
  const [codes, setCodes] = useState(null);
  const [email, setEmail] = useState("");
  const [expires, setExpires] = useState("");
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(null);

  const load = () => api.get("/admin/invite-codes").then((r) => setCodes(r.data.codes)).catch(() => setCodes([]));
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!email.trim() || !email.includes("@")) { toast.error("Inserisci un'email valida"); return; }
    setBusy(true);
    try {
      const body = { email: email.trim() };
      if (expires) body.expires_at = new Date(expires).toISOString();
      await api.post("/admin/invite-codes", body);
      toast.success("Codice invito creato");
      setEmail(""); setExpires(""); load();
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const disable = async (id) => {
    if (!window.confirm("Disattivare questo codice? Non potrà più essere usato.")) return;
    try { await api.post("/admin/invite-codes/disable", { id }); toast.success("Codice disattivato"); load(); }
    catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  };

  const copy = async (code) => {
    try { await navigator.clipboard.writeText(code); setCopied(code); setTimeout(() => setCopied(null), 1500); toast.success("Codice copiato"); }
    catch { toast.error("Copia non riuscita"); }
  };

  const fmt = (iso) => { if (!iso) return "—"; try { return new Date(iso).toLocaleDateString("it-IT", { day: "numeric", month: "short", year: "numeric" }); } catch { return "—"; } };

  if (!codes) return <Loader />;

  return (
    <div className="space-y-6">
      <div className="rounded-3xl bg-white/60 border border-anima-ink/5 p-6">
        <p className="font-serif text-2xl text-anima-ink mb-1">Inviti personali</p>
        <p className="text-sm text-anima-muted mb-5">Ogni codice dà il 50% di sconto sul primo periodo (mensile 4,95 € poi 9,90 €/mese; annuale 39,50 € poi 79 €/anno). È riservato all'email indicata e vale per un solo acquisto riuscito. Nessuna email viene inviata automaticamente.</p>
        <div className="space-y-3">
          <div>
            <label className="text-xs uppercase tracking-widest text-anima-muted">Email della persona invitata</label>
            <input data-testid="invite-create-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="nome@email.com"
              className="w-full mt-1 rounded-xl bg-white/70 border border-anima-ink/10 px-3 py-2.5 outline-none text-sm" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest text-anima-muted">Scadenza (facoltativa)</label>
            <input data-testid="invite-create-expiry" type="date" value={expires} onChange={(e) => setExpires(e.target.value)}
              className="w-full mt-1 rounded-xl bg-white/70 border border-anima-ink/10 px-3 py-2.5 outline-none text-sm" />
          </div>
          <button data-testid="invite-create-btn" onClick={create} disabled={busy}
            className="rounded-full bg-anima-sagedeep text-anima-ivory px-5 py-2.5 text-sm flex items-center gap-2 disabled:opacity-60">
            <Plus size={15} /> {busy ? "Creo..." : "Crea codice"}
          </button>
        </div>
      </div>

      <div className="space-y-3" data-testid="invite-list">
        {codes.length === 0 && <p className="text-sm text-anima-muted text-center py-6">Nessun codice creato finora.</p>}
        {codes.map((c) => {
          const [label, cls] = INVITE_STATUS[c.status] || INVITE_STATUS.available;
          return (
            <div key={c.id} data-testid={`invite-row-${c.code}`} className="rounded-2xl bg-white/60 border border-anima-ink/8 p-4">
              <div className="flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <p className="font-mono text-anima-ink text-sm truncate">{c.code}</p>
                  <p className="text-xs text-anima-muted truncate">{c.email}</p>
                </div>
                <span className={`text-[11px] px-2.5 py-1 rounded-full whitespace-nowrap ${cls}`}>{label}</span>
              </div>
              <div className="flex items-center gap-4 mt-2 text-[11px] text-anima-muted flex-wrap">
                <span>Scadenza: {fmt(c.expires_at)}</span>
                {c.status === "used" && <span>Piano: {PLAN_LABELS[c.plan_purchased] || c.plan_purchased || "—"}</span>}
                {c.status === "used" && <span>Usato il: {fmt(c.used_at)}</span>}
              </div>
              <div className="flex items-center gap-2 mt-3">
                <button data-testid={`invite-copy-${c.code}`} onClick={() => copy(c.code)}
                  className="rounded-full border border-anima-ink/15 text-anima-ink px-4 py-2 text-xs flex items-center gap-1.5">
                  {copied === c.code ? <Check size={13} /> : <Copy size={13} />} Copia
                </button>
                {c.status === "available" && (
                  <button data-testid={`invite-disable-${c.code}`} onClick={() => disable(c.id)}
                    className="rounded-full border border-anima-ink/15 text-anima-muted px-4 py-2 text-xs">Disattiva</button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}


function Translations() {
  const [shareUrl, setShareUrl] = useState("");
  const [savedShare, setSavedShare] = useState("");
  const [lang, setLang] = useState("");
  const [q, setQ] = useState("");
  const [items, setItems] = useState(null);
  const [langs, setLangs] = useState([]);
  const [total, setTotal] = useState(0);
  const [drafts, setDrafts] = useState({});

  const load = async () => {
    const params = {};
    if (lang) params.lang = lang;
    if (q.trim()) params.q = q.trim();
    const { data } = await api.get("/admin/translations", { params });
    setItems(data.items);
    setLangs(data.langs);
    setTotal(data.total);
    setDrafts({});
  };

  useEffect(() => {
    api.get("/admin/share-config").then((r) => { setShareUrl(r.data.url || ""); setSavedShare(r.data.url || ""); });
  }, []);

  // Ricarica elenco al cambio di filtro lingua.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [lang]);

  const saveShare = async () => {
    const { data } = await api.put("/admin/share-config", { url: shareUrl });
    setShareUrl(data.url); setSavedShare(data.url);
    toast.success("Link pubblico salvato");
  };

  const saveOne = async (id) => {
    const translated = drafts[id];
    if (translated === undefined) return;
    try {
      const { data } = await api.put(`/admin/translations/${id}`, { translated });
      setItems((prev) => prev.map((it) => (it.id === id ? data : it)));
      setDrafts((d) => { const n = { ...d }; delete n[id]; return n; });
      toast.success("Traduzione salvata");
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  };

  return (
    <div className="space-y-6">
      <div className="rounded-3xl bg-white/60 border border-anima-ink/5 p-6 space-y-3">
        <div>
          <p className="font-serif text-2xl text-anima-ink">Link pubblico di condivisione</p>
          <p className="text-sm text-anima-muted">Il collegamento definitivo dell'app, usato quando si condivide (senza link di anteprima). Lascia vuoto per usare l'indirizzo corrente.</p>
        </div>
        <div className="flex gap-2">
          <input
            data-testid="admin-share-url" value={shareUrl} onChange={(e) => setShareUrl(e.target.value)}
            placeholder="https://..."
            className="flex-1 rounded-xl bg-white/60 border border-anima-ink/10 px-3 py-2.5 outline-none text-sm" />
          <button data-testid="admin-share-save" onClick={saveShare} disabled={shareUrl === savedShare}
            className="rounded-full bg-anima-sagedeep text-anima-ivory px-5 py-2.5 text-sm disabled:opacity-50">Salva</button>
        </div>
      </div>

      <div className="rounded-3xl bg-white/60 border border-anima-ink/5 p-6">
        <div className="flex items-center justify-between mb-1">
          <p className="font-serif text-2xl text-anima-ink">Traduzioni AI</p>
          <span className="text-xs text-anima-muted">{total} voci</span>
        </div>
        <p className="text-sm text-anima-muted mb-4">Rivedi e correggi le traduzioni automatiche. Le correzioni manuali hanno la priorità e non vengono sovrascritte.</p>

        <div className="flex flex-wrap gap-2 mb-4">
          <button onClick={() => setLang("")} data-testid="admin-tr-lang-all"
            className={`rounded-full px-4 py-1.5 text-sm border ${lang === "" ? "bg-anima-sagedeep text-anima-ivory border-anima-sagedeep" : "bg-white/60 text-anima-ink border-anima-ink/10"}`}>Tutte</button>
          {langs.map((l) => (
            <button key={l} onClick={() => setLang(l)} data-testid={`admin-tr-lang-${l}`}
              className={`rounded-full px-4 py-1.5 text-sm border ${lang === l ? "bg-anima-sagedeep text-anima-ivory border-anima-sagedeep" : "bg-white/60 text-anima-ink border-anima-ink/10"}`}>
              {LANG_LABELS[l] || l}
            </button>
          ))}
        </div>

        <div className="flex gap-2 mb-5">
          <input
            data-testid="admin-tr-search" value={q} onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") load(); }}
            placeholder="Cerca nell'originale o nella traduzione…"
            className="flex-1 rounded-xl bg-white/60 border border-anima-ink/10 px-3 py-2.5 outline-none text-sm" />
          <button data-testid="admin-tr-search-btn" onClick={load} className="rounded-full bg-anima-ink/80 text-anima-ivory px-5 py-2.5 text-sm">Cerca</button>
        </div>

        {items === null ? <Loader /> : items.length === 0 ? (
          <p className="text-anima-muted text-sm py-8 text-center" data-testid="admin-tr-empty">Nessuna traduzione trovata.</p>
        ) : (
          <div className="space-y-3">
            {items.map((it) => {
              const draft = drafts[it.id] !== undefined ? drafts[it.id] : it.translated;
              const dirty = drafts[it.id] !== undefined && drafts[it.id] !== it.translated;
              return (
                <div key={it.id} className="rounded-2xl border border-anima-ink/10 bg-white/50 p-4" data-testid={`admin-tr-row-${it.id}`}>
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-[10px] uppercase tracking-widest rounded-full bg-anima-sage/30 text-anima-ink px-2 py-0.5">{LANG_LABELS[it.lang] || it.lang}</span>
                    {it.manual && <span className="text-[10px] uppercase tracking-widest rounded-full bg-anima-gold/30 text-anima-golddeep px-2 py-0.5" data-testid={`admin-tr-manual-${it.id}`}>Manuale</span>}
                  </div>
                  <p className="text-[13px] text-anima-muted leading-snug mb-2"><span className="text-anima-ink/50">IT:</span> {it.source}</p>
                  <textarea
                    data-testid={`admin-tr-input-${it.id}`}
                    value={draft}
                    onChange={(e) => setDrafts((d) => ({ ...d, [it.id]: e.target.value }))}
                    rows={2}
                    className="w-full rounded-xl bg-white/70 border border-anima-ink/10 px-3 py-2 outline-none text-sm leading-relaxed resize-none" />
                  <div className="flex justify-end mt-2">
                    <button
                      data-testid={`admin-tr-save-${it.id}`} onClick={() => saveOne(it.id)} disabled={!dirty}
                      className="rounded-full bg-anima-sagedeep text-anima-ivory px-5 py-2 text-sm disabled:opacity-40">Salva</button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
